<template lang="pug">
TheCommentSkeleton(v-if="commentQuery.isPending.value")

TheNotFound(v-else-if="is404")
TheError(
  v-else-if="error"
  title="Failed to load comment"
  :message="error.message"
  :stack="error.stack"
  @retry="commentQuery.refetch"
  @report="() => report(error)"
)

TheComment(
  v-else-if="comment"
  :nid="nid"
  :commentary-type="commentaryType"
  :detail-level-weight="detailLevelWeight"
  :ranges="ranges"
  :heading="comment.ss_heading"
  :group="comment.ss_group"
  :created="new Date(comment.ds_created)"
  :changed="new Date(comment.ds_changed)"
  :body="body"
  :contributions-by-role="contributionsByRole"
  :commentary-filters="commentaryFilters"
  :next-result="position.nextResult.value"
  :previous-result="position.previousResult.value"
  :local-tid="localTid"
  :language="comment.ss_search_api_language"
  :available-languages="availableLanguages"
)
  slot

div(v-else)
  div.surface
    VContainer.pb-6.pt-6.text-center
      HH.text-h1.mt-8.mb-6 {{ i18n.t("TheComment.NotAvailableIn", { name: commentLocaleName }) }}
  HSection(v-if="otherLanguagesAvailable.length > 0")
    VContainer.pb-6.pt-6
      HH.text-h2.section-title-2 {{ i18n.t("TheComment.AvailableInOtherLanguages") }}
      CommentsOtherLanguageTeaser(
        :nid="nid"
        :languages="otherLanguagesAvailable"
        :local-tid="localTid"
      )
</template>

<script setup lang="ts">
import type { VidRange, VidRangeData } from "~/units/scripture";
import { createVidRange } from "~/units/scripture";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { useCommentaryFiltersStore } from "~/stores/useCommentaryFiltersStore";
import { useLastCommentListStore } from "~/stores/useLastCommentListStore";
import { usePreferencesStore } from "~/stores/usePreferencesStore";
import { useCachedCommentaryQuery } from "~/composables/useCommentaryQuery";
import { injectRequired } from "~/injectRequired";
import { LogFunctionKey, SolrClientKey } from "~/injectionKeys";
import { getCommentaryTypeOrFallback } from "~/static/taxonomy/commentaryTypes";
import type { KohaMentionItem } from "~/components/commentNodeKoha.types";
import type { Dict } from "~/types";
import { parseJsonItemsAndSkipFailures } from "~/units/json";
import TheComment from "~/components/TheComment.vue";
import { localesByCode } from "~/i18n/locales";
import { never } from "~/never";
import { useCommentarySearchResultsPosition } from "~/composables/useCommentarySearchResultsPosition";
import HH from "~/headings/components/HH.vue";
import HSection from "~/headings/components/HSection.vue";
import CommentsOtherLanguageTeaser from "~/components/CommentsOtherLanguageTeaser.vue";
import { useReport } from "~/logging/report";
import type {
  ProcessedCommentaryComment,
  ProcessedCommentaryImage,
} from "~/composables/processCommentarySearchResults/types";

const report = useReport();

const solrClient = injectRequired(SolrClientKey);
const log = injectRequired(LogFunctionKey);

const i18n = useI18n();
const locale = i18n.locale;

const localeConfig = computed(
  () => localesByCode.get(i18n.locale.value) ?? never("Locale not found."),
);
const localTid = computed<string>(() => localeConfig.value.defaultTid);

const runtimeConfig = useRuntimeConfig();

type Props = {
  nid: number;
  language: string;
};

const props = defineProps<Props>();

const nid = computed(() => props.nid);

const commentLanguage = computed(() => props.language);

const commentLocaleName = computed<string>(() => {
  return (
    (localesByCode.get(commentLanguage.value) ?? localeConfig.value).name ?? ""
  );
});

const commentQuery = useNode(
  solrClient,
  nid,
  commentLanguage,
  [
    "sm_type_machine_name",
    "its_detail_weight",
    "zs_body_full_html",
    "zs_body_summary_html",
    "zm_body_koha_mentions",
    "zm_scripture",
    "ss_heading",
    "ss_group",
    "ds_changed",
    "ds_created",
    "ss_search_api_language",
    "zm_author_name",
    "zm_gleaner_name",
    "zm_linguist_name",
    "zm_transcriber_name",
    "zm_translator_name",
  ],
  "commentary_comments",
  log,
);

const comment = computed(() => commentQuery.data.value?.value?.doc ?? null);
const availableLanguages = computed(
  () => commentQuery.data.value?.value?.languages ?? [],
);
const error = computed(
  () => commentQuery.error.value ?? commentQuery.data.value?.error,
);

const is404 = computed(
  () =>
    error.value?.statusCode === 404 ||
    (!error.value && !comment.value && availableLanguages.value.length === 0),
);

const otherLanguagesAvailable = computed(() => {
  return availableLanguages.value.filter(
    (language) => language !== commentLanguage.value,
  );
});

const commentaryType = computed<{
  displayName: string;
  machineName: string;
}>(() => {
  const term = getCommentaryTypeOrFallback(
    comment.value?.sm_type_machine_name?.[0] || "",
  );
  return {
    displayName: term.name[locale.value] ?? "",
    machineName: term.machineName,
  };
});

const detailLevelWeight = computed<number>(
  () => comment.value?.its_detail_weight ?? 0,
);

const body = computed<{
  full: string;
  summary: string;
  kohaMentions: KohaMentionItem[];
}>(() => ({
  full: comment.value?.zs_body_full_html ?? "",
  summary: comment.value?.zs_body_summary_html ?? "",
  kohaMentions: parseJsonItemsAndSkipFailures(
    comment.value?.zm_body_koha_mentions ?? [],
  ) as KohaMentionItem[],
}));

const contributionsByRole = computed<Dict<string[]>>(() => {
  return {
    Author: comment.value?.zm_author_name ?? [],
    Gleaner: comment.value?.zm_gleaner_name ?? [],
    Linguist: comment.value?.zm_linguist_name ?? [],
    Transcriber: comment.value?.zm_transcriber_name ?? [],
    Translator: comment.value?.zm_translator_name ?? [],
  };
});

const ranges = computed<VidRange[]>(() => {
  return parseJsonItemsAndSkipFailures(comment.value?.zm_scripture ?? []).map(
    (item) => createVidRange(item as VidRangeData),
  );
});

const commentaryFiltersStore = useCommentaryFiltersStore();
const defaultCommentaryFilters: CommentaryFilters = {
  vids: runtimeConfig.public.commentary.defaultVids,
  detailLevelWeight: runtimeConfig.public.commentary.defaultDetailLevelWeight,
  commentaryTypeMachineName: runtimeConfig.public.commentary.defaultType,
};
const commentaryFilters = computed(() =>
  commentaryFiltersStore.getFilters(defaultCommentaryFilters),
);
const preferencesStore = usePreferencesStore();
const languages = computed(() =>
  preferencesStore.getLanguages(i18n.locale.value),
);
const commentaryQuery = useCachedCommentaryQuery(commentaryFilters, languages);

const { commentList } = useLastCommentListStore();

const position = useCommentarySearchResultsPosition(
  nid,
  computed<(ProcessedCommentaryComment | ProcessedCommentaryImage)[]>(
    () =>
      (!commentList || commentList.length === 0
        ? commentaryQuery?.value?.flatResults
        : commentList
      )?.filter((comment) => comment.type === "comment") ?? [],
  ),
);
defineExpose({ position });

onServerPrefetch(async () => {
  await commentQuery.suspense();
});
</script>
